import { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useProduct } from '@ibk/shared-config';
import { useTranslation } from '@ibk/shared-translation';
import { ProductAlias } from '@ibk/shared-types';
import { formatCurrency, formatShortDateWithoutTimezone } from '@ibk/shared-utils';
import { AccountsPayableHistoryEvent, BillType, OperationCode, PixKeyType } from '../../enums';
import { useGetAccountsPayablePixKeys } from '../useGetAccountsPayablePixKeys';
import { useGetFinancialInstitutions } from '../useGetFinancialInstitutions';
import { useGetPaymentDetails } from '../useGetPaymentDetails';
import { usePostPix } from '../usePostPix';
const STEP_EMPLOYEES = 0;
const STEP_REVISION = 1;
const STEP_PROCESSING = 2;
function isAccountPayableProcessing(ap) {
    return ap.lastHistoryEvent === AccountsPayableHistoryEvent.PROCESSING;
}
function isAccountPayableAwaitingApproval(ap) {
    return ap.lastHistoryEvent === AccountsPayableHistoryEvent.AWAITING_APPROVAL;
}
function isAccountPayableReviewRequired(ap) {
    return ap.lastHistoryEvent === AccountsPayableHistoryEvent.REVIEW_REQUIRED;
}
function isAccountPayableLocked(ap) {
    return isAccountPayableProcessing(ap) || isAccountPayableAwaitingApproval(ap);
}
export function usePixEmployeeAccountPayable(batchOfAccountsPayable, operationCode, totalAmount) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { data: product } = useProduct(ProductAlias.CONTA_DIGITAL);
    const { financialInstitutions, getFinancialInstitutions, loading: loadingGetFinancialInstitutions, } = useGetFinancialInstitutions();
    const { getAccountsPayablePixKeys, accountsPayablePixKeys, loading: loadingGetAccountsPayablePixKeys, errors: errorsGetAccountsPayablePixKeys, } = useGetAccountsPayablePixKeys();
    const { postPix, pixBatchAccepted, pixBatchProcessingError, pixBatchLoading } = usePostPix();
    const { getPaymentDetails, loading: loadingGetPaymentDetails } = useGetPaymentDetails();
    const [step, setStep] = useState(STEP_EMPLOYEES);
    const [showAccountsPayableNotFoundError, setShowAccountsPayableNotFoundError] = useState(false);
    const [showInfoAccountPayableInvalid, setShowInfoAccountPayableInvalid] = useState(false);
    const [showErrorAccountPayableInvalid, setShowErrorAccountPayableInvalid] = useState(false);
    const [showPixKeysNotFound, setShowPixKeysNotFound] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [showPinKeyboard, setShowPinKeyboard] = useState(false);
    const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] = useState(false);
    const [showAlert, setShowAlert] = useState(undefined);
    const [showPixTimeoutError, setShowPixTimeoutError] = useState(false);
    const [allAccountsPayableSelected, setAllAccountsPayableSelected] = useState(true);
    const [accountPayableToUpdate, setAccountPayableToUpdate] = useState();
    const [accountsPayable, setAccountsPayable] = useState();
    const [selectedAccountsPayable, setSelectedAccountsPayable] = useState();
    const [scheduleDate, setScheduleDate] = useState();
    const [transactionStartAt, setTransactionStartAt] = useState(new Date().toISOString());
    const [showBankDetailsModal, setShowBankDetailsModal] = useState(false);
    const [paymentDetails, setPaymentDetails] = useState(undefined);
    const [pendingPaymentDetails, setPendingPaymentDetails] = useState(undefined);
    const [bankValue, setBankValue] = useState(undefined);
    const [showLimitExceededModal, setShowLimitExceededModal] = useState(false);
    useEffect(() => {
        if (!batchOfAccountsPayable || batchOfAccountsPayable.length === 0 || !operationCode || !totalAmount) {
            setShowAccountsPayableNotFoundError(true);
        }
        else {
            const selectableAccountsPayable = batchOfAccountsPayable.filter(ap => !isAccountPayableLocked(ap));
            setAccountsPayable(batchOfAccountsPayable);
            setSelectedAccountsPayable(selectableAccountsPayable);
            setAllAccountsPayableSelected(selectableAccountsPayable.length > 0);
            if (hasInvalidAccountPayable(selectableAccountsPayable)) {
                setShowInfoAccountPayableInvalid(true);
            }
        }
    }, []);
    useEffect(() => {
        if (!paymentDetails)
            return;
        if (paymentDetails.paymentDetails.details.keyType === PixKeyType.BANK_ACCOUNT) {
            if (!financialInstitutions)
                return;
            const foundBank = financialInstitutions.find(institution => institution.code === paymentDetails.paymentDetails.details.counterparty?.bank);
            setBankValue(foundBank);
            setShowBankDetailsModal(true);
        }
        else {
            setShowBankDetailsModal(true);
        }
    }, [paymentDetails, financialInstitutions]);
    useEffect(() => {
        if (pendingPaymentDetails && financialInstitutions && financialInstitutions.length > 0) {
            setPaymentDetails(pendingPaymentDetails);
            setPendingPaymentDetails(undefined);
        }
    }, [pendingPaymentDetails, financialInstitutions]);
    useEffect(() => {
        if (errorsGetAccountsPayablePixKeys) {
            setAccountsPayable(accountsPayable?.map(value => errorsGetAccountsPayablePixKeys.some(item => item.value.id === value.id) ? { ...value, error: true } : value));
            setSelectedAccountsPayable(selectedAccountsPayable?.map(value => errorsGetAccountsPayablePixKeys.some(item => item.value.id === value.id) ? { ...value, error: true } : value));
            setShowPixKeysNotFound(true);
        }
    }, [errorsGetAccountsPayablePixKeys]);
    useEffect(() => {
        if (accountsPayablePixKeys && !errorsGetAccountsPayablePixKeys) {
            setStep(STEP_REVISION);
        }
    }, [accountsPayablePixKeys, errorsGetAccountsPayablePixKeys]);
    useEffect(() => {
        if (pixBatchAccepted) {
            setShowPinKeyboard(false);
            setStep(STEP_PROCESSING);
        }
    }, [pixBatchAccepted]);
    useEffect(() => {
        if (pixBatchProcessingError && !pixBatchAccepted) {
            onError(pixBatchProcessingError);
        }
    }, [pixBatchProcessingError, pixBatchAccepted]);
    const showLoading = useMemo(() => {
        return (loadingGetAccountsPayablePixKeys || loadingGetPaymentDetails || loadingGetFinancialInstitutions || pixBatchLoading);
    }, [loadingGetAccountsPayablePixKeys, loadingGetPaymentDetails, loadingGetFinancialInstitutions, pixBatchLoading]);
    const showStepperHorizontal = useMemo(() => {
        return !showAccountsPayableNotFoundError && !showPixTimeoutError && step !== STEP_PROCESSING;
    }, [showAccountsPayableNotFoundError, showPixTimeoutError, step]);
    const showEmployeeStep = useMemo(() => {
        return step === STEP_EMPLOYEES && !showLoading;
    }, [step, showLoading]);
    const showRevisionStep = useMemo(() => {
        return step === STEP_REVISION && !showLoading;
    }, [step, showLoading]);
    const showProcessingStep = useMemo(() => {
        return step === STEP_PROCESSING && !showLoading;
    }, [step, showLoading]);
    const totalAccountsPayable = useMemo(() => {
        return accountsPayable?.length ?? 0;
    }, [accountsPayable]);
    const totalAccountsPayableSelected = useMemo(() => {
        return selectedAccountsPayable?.length ?? 0;
    }, [selectedAccountsPayable]);
    const totalAmountSelected = useMemo(() => {
        return formatCurrency(selectedAccountsPayable?.reduce((total, accountPayable) => total + (accountPayable.amount ?? 0), 0) ?? 0);
    }, [selectedAccountsPayable]);
    const totalAccountsPayablePixKeysNotFound = useMemo(() => {
        return errorsGetAccountsPayablePixKeys?.length ?? 0;
    }, [errorsGetAccountsPayablePixKeys]);
    const totalValue = useMemo(() => {
        return formatCurrency(accountsPayablePixKeys?.reduce((total, accountPayable) => total + (accountPayable.amount ?? 0), 0) ?? 0);
    }, [accountsPayablePixKeys]);
    const totalEmployees = useMemo(() => {
        return accountsPayablePixKeys?.length ?? 0;
    }, [accountsPayablePixKeys]);
    const paymentDate = useMemo(() => {
        return scheduleDate ? formatShortDateWithoutTimezone(scheduleDate) : format(new Date(), 'dd/MM/yyyy');
    }, [scheduleDate]);
    const paymentType = useMemo(() => {
        if (operationCode) {
            return {
                [OperationCode.PAYROLL]: t('batch-of-payroll'),
                [OperationCode.EMPLOYEE_13TH_SALARY]: t('batch-of-thirteenth-salary'),
            }[operationCode];
        }
    }, [operationCode]);
    const processingStepAmount = useMemo(() => {
        return formatCurrency(totalAmount ?? 0);
    }, [totalAmount]);
    const processingStepDate = useMemo(() => {
        return format(new Date(transactionStartAt), 'dd/MM/yyyy HH:mm');
    }, [transactionStartAt]);
    const processingStepType = useMemo(() => {
        return t('pix');
    }, []);
    const pendingEmployees = useMemo(() => {
        if (accountsPayable && accountsPayablePixKeys) {
            return accountsPayable?.length - accountsPayablePixKeys?.length;
        }
    }, [accountsPayable, accountsPayablePixKeys]);
    const pendingValue = useMemo(() => {
        if (accountsPayable && accountsPayablePixKeys) {
            return formatCurrency(accountsPayable?.reduce((total, accountPayable) => total + (accountPayable.amount ?? 0), 0) -
                accountsPayablePixKeys?.reduce((total, accountPayable) => total + (accountPayable.amount ?? 0), 0));
        }
    }, [accountsPayable, accountsPayablePixKeys]);
    const showPendingEmployeesInfo = useMemo(() => {
        if (pendingEmployees) {
            return pendingEmployees > 0;
        }
    }, [pendingEmployees]);
    const isAccountPayableSelected = useCallback((accountPayable) => {
        return selectedAccountsPayable?.some(value => value.id === accountPayable.id);
    }, [accountsPayable, selectedAccountsPayable]);
    const isAccountPayableInvalid = useCallback((accountPayable) => {
        return (accountPayable.error === true ||
            !accountPayable.employeePaymentDetails ||
            isAccountPayableReviewRequired(accountPayable));
    }, [accountsPayable, selectedAccountsPayable]);
    const totalSelectableAccountsPayable = useMemo(() => {
        return accountsPayable?.filter(ap => !isAccountPayableLocked(ap)).length ?? 0;
    }, [accountsPayable]);
    const areAllAccountsPayableUnavailable = useMemo(() => {
        if (!accountsPayable || accountsPayable.length === 0)
            return false;
        return accountsPayable.every(ap => isAccountPayableLocked(ap) || isAccountPayableInvalid(ap));
    }, [accountsPayable, isAccountPayableInvalid]);
    const isNextButtonDisabledOnEmployeesStep = useMemo(() => {
        if (areAllAccountsPayableUnavailable)
            return true;
        if (!selectedAccountsPayable || selectedAccountsPayable.length === 0)
            return true;
        return selectedAccountsPayable.some(ap => isAccountPayableInvalid(ap));
    }, [selectedAccountsPayable, areAllAccountsPayableUnavailable, isAccountPayableInvalid]);
    function onError(error) {
        setShowPinKeyboard(false);
        if (error === t('pix-limit-exceeded')) {
            setShowLimitExceededModal(true);
        }
        else if (error === t('pix-timeout-error')) {
            setShowPixTimeoutError(true);
        }
        else if (error === t('insufficient-balance-modal-title')) {
            setShowInsufficientBalanceModal(true);
        }
        else {
            setShowAlert({
                message: error,
            });
        }
    }
    function hasInvalidAccountPayable(values) {
        return values?.some(accountPayable => accountPayable.error || !accountPayable.employeePaymentDetails);
    }
    function onPressAccountPayableCheckBox(accountPayable) {
        if (isAccountPayableLocked(accountPayable))
            return;
        if (isAccountPayableSelected(accountPayable)) {
            setSelectedAccountsPayable(selectedAccountsPayable?.filter(value => value.id !== accountPayable.id));
            setAllAccountsPayableSelected(false);
        }
        else {
            const newSelectedAccountsPayable = [...(selectedAccountsPayable ?? []), accountPayable];
            setSelectedAccountsPayable(newSelectedAccountsPayable);
            setAllAccountsPayableSelected(newSelectedAccountsPayable.length === totalSelectableAccountsPayable);
        }
    }
    async function onPressAccountPayableButton(accountPayable) {
        if (isAccountPayableLocked(accountPayable))
            return;
        setAccountPayableToUpdate(accountPayable);
        if (!accountPayable?.employeePaymentDetails) {
            setPaymentDetails(undefined);
            setShowBankDetailsModal(true);
            return;
        }
        const fetchedPaymentDetails = await getPaymentDetails(accountPayable.employeePaymentDetails);
        if (!fetchedPaymentDetails) {
            setPaymentDetails(undefined);
            setShowBankDetailsModal(true);
            return;
        }
        if (fetchedPaymentDetails.paymentDetails.details.keyType === PixKeyType.BANK_ACCOUNT) {
            await getFinancialInstitutions();
            setPendingPaymentDetails(fetchedPaymentDetails);
        }
        else {
            setPaymentDetails(fetchedPaymentDetails);
        }
    }
    function onPressCloseBankDetailsModal() {
        setShowBankDetailsModal(false);
        setPaymentDetails(undefined);
        setAccountPayableToUpdate(undefined);
    }
    function updateAccountPayable(newPaymentDetails, accountPayableList) {
        return accountPayableList?.map(currentAccountPayable => {
            if (currentAccountPayable.id !== accountPayableToUpdate?.id) {
                return currentAccountPayable;
            }
            let newPaymentDetailsPix;
            if (currentAccountPayable.employeePaymentDetailsInfo?.paymentDetails?.type === BillType.ACCOUNT) {
                newPaymentDetailsPix = {
                    type: BillType.ACCOUNT,
                    details: {
                        bank: newPaymentDetails.paymentDetails.details.counterparty?.bank,
                        branch: newPaymentDetails.paymentDetails.details.counterparty?.branch,
                        account: newPaymentDetails.paymentDetails.details.counterparty?.account,
                        accountType: newPaymentDetails.paymentDetails.details.counterparty?.accountType,
                    },
                };
            }
            else {
                newPaymentDetailsPix = {
                    type: BillType.PIX,
                    details: {
                        key: newPaymentDetails.paymentDetails.details.key,
                        keyType: newPaymentDetails.paymentDetails.details.keyType,
                        counterparty: newPaymentDetails.paymentDetails.details.keyType === PixKeyType.BANK_ACCOUNT
                            ? newPaymentDetails.paymentDetails.details.counterparty
                            : undefined,
                    },
                };
            }
            return {
                ...currentAccountPayable,
                employeePaymentDetails: newPaymentDetails.id,
                employeePaymentDetailsInfo: {
                    id: newPaymentDetails.id,
                    party: newPaymentDetails.party,
                    documentType: newPaymentDetails.documentType,
                    documentNumber: newPaymentDetails.documentNumber,
                    paymentDetails: newPaymentDetailsPix,
                },
                error: false,
                lastHistoryEvent: isAccountPayableReviewRequired(currentAccountPayable)
                    ? undefined
                    : currentAccountPayable.lastHistoryEvent,
            };
        });
    }
    function onSaveBankDetailsModal(newPaymentDetails) {
        setAccountsPayable(updateAccountPayable(newPaymentDetails, accountsPayable));
        const updatedSelectedAccountsPayable = updateAccountPayable(newPaymentDetails, selectedAccountsPayable);
        setSelectedAccountsPayable(updateAccountPayable(newPaymentDetails, selectedAccountsPayable));
        if (!hasInvalidAccountPayable(updatedSelectedAccountsPayable)) {
            setShowInfoAccountPayableInvalid(false);
            setShowErrorAccountPayableInvalid(false);
        }
        setTimeout(() => {
            setPaymentDetails(undefined);
            setShowBankDetailsModal(false);
            setAccountPayableToUpdate(undefined);
        }, 3000);
    }
    function onPressBackOnEmployeeStep() {
        navigate(-1);
    }
    function onPressNextOnEmployeeStep() {
        if (hasInvalidAccountPayable(selectedAccountsPayable)) {
            setShowInfoAccountPayableInvalid(false);
            setShowErrorAccountPayableInvalid(true);
            return;
        }
        getAccountsPayablePixKeys(selectedAccountsPayable);
    }
    function onPressSelectAll() {
        if (areAllAccountsPayableUnavailable)
            return;
        if (allAccountsPayableSelected) {
            setSelectedAccountsPayable(undefined);
        }
        else {
            setSelectedAccountsPayable(accountsPayable?.filter(ap => !isAccountPayableLocked(ap)));
        }
        setAllAccountsPayableSelected(!allAccountsPayableSelected);
    }
    function onPressProceedWithoutThisPayment() {
        setShowPixKeysNotFound(false);
        setStep(STEP_REVISION);
    }
    function onPressBackOnRevisionStep() {
        setStep(STEP_EMPLOYEES);
    }
    function onPressNextOnRevisionStep() {
        const availableBalance = product?.metadata?.balance?.amount;
        const totalToPay = accountsPayablePixKeys?.reduce((total, accountPayable) => total + (accountPayable.amount ?? 0), 0) ?? 0;
        if (!scheduleDate && availableBalance !== undefined && totalToPay > availableBalance) {
            setShowInsufficientBalanceModal(true);
        }
        else {
            setShowPinKeyboard(true);
        }
    }
    function onPressConfirmOnPinKeyBoard(pin) {
        postPix(pin, transactionStartAt, scheduleDate, undefined, accountsPayablePixKeys?.map(value => value.pixKey), paymentType);
    }
    function onPressCloseOnProcessingStep() {
        navigate('/conta-pj');
    }
    function onPressViewPaymentsOnProcessingStep() {
        navigate('/pagamentos');
    }
    return {
        paymentDetails,
        showBankDetailsModal,
        bankValue,
        setShowBankDetailsModal,
        onPressCloseBankDetailsModal,
        onSaveBankDetailsModal,
        showAccountsPayableNotFoundError,
        setShowAccountsPayableNotFoundError,
        step,
        setStep,
        selectedAccountsPayable,
        setSelectedAccountsPayable,
        allAccountsPayableSelected,
        setAllAccountsPayableSelected,
        accountsPayable,
        setAccountsPayable,
        accountPayableToUpdate,
        setAccountPayableToUpdate,
        showInfoAccountPayableInvalid,
        setShowInfoAccountPayableInvalid,
        showErrorAccountPayableInvalid,
        setShowErrorAccountPayableInvalid,
        showPixKeysNotFound,
        setShowPixKeysNotFound,
        showCalendar,
        setShowCalendar,
        scheduleDate,
        setScheduleDate,
        showPinKeyboard,
        setShowPinKeyboard,
        showInsufficientBalanceModal,
        setShowInsufficientBalanceModal,
        transactionStartAt,
        setTransactionStartAt,
        showAlert,
        setShowAlert,
        showPixTimeoutError,
        setShowPixTimeoutError,
        showStepperHorizontal,
        showLimitExceededModal,
        setShowLimitExceededModal,
        showEmployeeStep,
        showRevisionStep,
        showProcessingStep,
        showLoading,
        totalAccountsPayable,
        totalAccountsPayableSelected,
        totalAmountSelected,
        isNextButtonDisabledOnEmployeesStep,
        totalAccountsPayablePixKeysNotFound,
        totalValue,
        totalEmployees,
        paymentType,
        paymentDate,
        processingStepAmount,
        processingStepDate,
        processingStepType,
        pendingEmployees,
        pendingValue,
        showPendingEmployeesInfo,
        isAccountPayableSelected,
        isAccountPayableInvalid,
        isAccountPayableProcessing,
        isAccountPayableAwaitingApproval,
        isAccountPayableReviewRequired,
        isAccountPayableLocked,
        areAllAccountsPayableUnavailable,
        onPressBackOnEmployeeStep,
        onPressNextOnEmployeeStep,
        onPressAccountPayableCheckBox,
        onPressAccountPayableButton,
        onPressSelectAll,
        onPressProceedWithoutThisPayment,
        onPressBackOnRevisionStep,
        onPressNextOnRevisionStep,
        onPressConfirmOnPinKeyBoard,
        onPressCloseOnProcessingStep,
        onPressViewPaymentsOnProcessingStep,
    };
}
